import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { normalizeBaseUrl } from '../utils/url.utils';
import { AuthService } from '../services/auth.service';
import { ToastService } from '../services/toast.service';

const getReturnUrl = (): string =>
  `${window.location.pathname}${window.location.search}${window.location.hash}`;

/**
 * Handles auth-related HTTP errors for API requests.
 *
 * 401 flow:
 * - attempt one forced silent token refresh
 * - retry request once if a refreshed token is available
 * - if the refresh fails outright, or the retried request also 401s, trigger a
 *   single-flight interactive sign-in (preserving returnUrl for authenticated
 *   users); no further retry or refresh attempt is made either way (no loop)
 *
 * 403 flow:
 * - show access denied message and navigate to access denied page
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const toastService = inject(ToastService);
  const apiBaseUrl = normalizeBaseUrl(environment.apiBaseUrl);
  const isApiRequest = req.url.startsWith(apiBaseUrl);

  const triggerReauth = () => {
    if (!authService.reauthInFlight()) {
      toastService.error('Your session has expired. Please log in again.');
      authService.triggerReauthentication(
        authService.isAuthenticated() ? getReturnUrl() : undefined,
      );
    }
  };

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Handle 401 Unauthorized errors (expired or invalid session)
      if (error.status === 401 && isApiRequest) {
        return authService.refreshAccessTokenSilently().pipe(
          switchMap((token) => {
            if (!token) {
              triggerReauth();
              return throwError(() => error);
            }

            return next(
              req.clone({
                setHeaders: {
                  Authorization: `Bearer ${token}`,
                },
              }),
            ).pipe(
              catchError((retryError: HttpErrorResponse) => {
                if (retryError.status === 401) {
                  triggerReauth();
                }
                return throwError(() => retryError);
              }),
            );
          }),
        );
      }

      // Handle access denied (group allowlist).
      // Skip /me/role: a 403 there means "no role assigned yet" — the guard handles it.
      if (error.status === 403 && !req.url.includes('/me/role')) {
        toastService.error('Access denied. Contact your administrator for access.');
        void router.navigate(['/access-denied']);
      }

      // Re-throw the error for component-level handling if needed
      return throwError(() => error);
    }),
  );
};
